import React, { useEffect, useState } from "react";
import HeartLoader from "@/components/HeartLoader";
import { motion } from "framer-motion";
import { User, Loader2, Sparkles, Wand2, Save, Copy, Share2, UserPlus, MapPin, Plus, X, Building2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { FileUpload } from "@/components/FileUpload";
import { KindCircle } from "@/components/KindCircle";
import { toast } from "sonner";

const ORG_SIZES = [
  { key: "personal", label: "Individual / Solo", desc: "Just me" },
  { key: "small", label: "Small Org", desc: "Startup / small team" },
  { key: "mid", label: "Mid-size Org", desc: "Scaling company" },
  { key: "enterprise", label: "Enterprise", desc: "Large / global" },
];

export default function Profile() {
  const { user, setUser, lang } = useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({ followers: 0, following: 0, posts: 0 });
  const [p, setP] = useState({ name: "", headline: "", bio: "", avatar_url: "", skills: [], location: "" });
  const [skillInput, setSkillInput] = useState("");
  const [genOpen, setGenOpen] = useState(false);
  const [orgSize, setOrgSize] = useState(user?.account_type || "personal");
  useEffect(() => { if (user?.account_type) setOrgSize(user.account_type); }, [user]);
  const chooseOrgSize = async (key) => {
    setOrgSize(key);
    try { await api.post("/account/type", { account_type: key }); if (setUser) setUser((u) => u ? { ...u, account_type: key } : u); toast.success("Org size updated"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/profile/me");
        setP({ ...data.profile, skills: data.profile.skills || [] });
        setStats(data.stats);
      } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, [user]);

  const set = (k) => (e) => setP((prev) => ({ ...prev, [k]: e.target.value }));
  const addSkill = () => { const s = skillInput.trim(); if (s) { setP((prev) => prev.skills.includes(s) ? prev : { ...prev, skills: [...prev.skills, s] }); setSkillInput(""); } };
  const removeSkill = (s) => setP((prev) => ({ ...prev, skills: prev.skills.filter((x) => x !== s) }));

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/profile", p); setP({ ...data.profile, skills: data.profile.skills || [] }); toast.success("Profile saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const inviteLink = p.referral_code ? `${window.location.origin}/auth?invite=${p.referral_code}` : "";
  const copyInvite = () => { navigator.clipboard.writeText(inviteLink); toast.success("Invite link copied — share it to grow your connections & earnings!"); };
  const shareInvite = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Join me on My Kind", url: inviteLink }); } catch {} }
    else copyInvite();
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 pt-40 text-center">
        <User size={40} className="mx-auto text-brand mb-3" />
        <h1 className="font-serif text-3xl">Your Profile</h1>
        <p className="text-sec mt-2 text-sm">Sign in to create your profile, invite connections and share your work.</p>
        <a href="/auth" className="btn-primary h-11 px-6 inline-flex items-center mt-4">Sign in</a>
      </div>
    );
  }
  if (loading) return <div className="pt-40 text-center"><HeartLoader /></div>;

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><User size={14} /> My Profile</span>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-3">Who you are</h1>

      <div className="grid lg:grid-cols-3 gap-4 mt-8">
        <div className="rud-card rounded-2xl p-6 text-center">
          <div className="w-24 h-24 rounded-full mx-auto overflow-hidden bg-elevated border border-rud grid place-items-center">
            {p.avatar_url ? <img src={p.avatar_url} alt="" className="w-full h-full object-cover" /> : <span className="text-3xl font-bold">{(p.name || "?")[0]?.toUpperCase()}</span>}
          </div>
          <div className="font-serif text-2xl mt-3">{p.name || "Your name"}</div>
          {p.headline && <div className="text-sm text-sec mt-1">{p.headline}</div>}
          {p.location && <div className="text-xs text-sec mt-1 flex items-center justify-center gap-1"><MapPin size={12} /> {p.location}</div>}
          <div className="flex justify-center gap-4 mt-4 text-sm">
            <div><div className="font-bold" data-testid="profile-followers">{stats.followers}</div><div className="text-xs text-sec">Connections</div></div>
            <div><div className="font-bold">{stats.posts}</div><div className="text-xs text-sec">Posts</div></div>
            <div><div className="font-bold">{stats.following}</div><div className="text-xs text-sec">Following</div></div>
          </div>
        </div>

        <div className="lg:col-span-2 rud-card rounded-2xl p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-xl">Edit profile</h3>
            <button data-testid="profile-generate-open" onClick={() => setGenOpen(true)} className="btn-ghost rounded-full h-9 px-4 flex items-center gap-2 text-sm"><Wand2 size={14} className="text-brand" /> My Kind, write it for me</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <input data-testid="profile-name" value={p.name} onChange={set("name")} placeholder="Full name" className="rud-input rounded-lg px-3 py-2.5 text-sm" />
            <input data-testid="profile-location" value={p.location} onChange={set("location")} placeholder="Location" className="rud-input rounded-lg px-3 py-2.5 text-sm" />
          </div>
          <div className="flex gap-2 items-center">
            <input data-testid="profile-avatar" value={p.avatar_url} onChange={set("avatar_url")} placeholder="Profile photo URL" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
            <FileUpload category="avatar" accept="image/*" label="Upload" testId="profile-avatar-upload" onUploaded={(f) => setP((prev) => ({ ...prev, avatar_url: f.absoluteUrl }))} />
          </div>
          <input data-testid="profile-headline" value={p.headline} onChange={set("headline")} placeholder="Headline (e.g. Designer & founder)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <textarea data-testid="profile-bio" value={p.bio} onChange={set("bio")} rows={4} placeholder="Short bio…" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm resize-none" />
          <div>
            <div className="flex gap-2">
              <input data-testid="profile-skill-input" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())} placeholder="Add a skill / interest" className="rud-input flex-1 rounded-lg px-3 py-2.5 text-sm" />
              <button onClick={addSkill} className="btn-ghost rounded-lg h-10 w-10 grid place-items-center"><Plus size={16} /></button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {p.skills.map((s) => <span key={s} className="text-xs bg-elevated border border-rud rounded-full px-3 py-1.5 flex items-center gap-1.5">{s} <button onClick={() => removeSkill(s)} className="hover:text-red-400"><X size={12} /></button></span>)}
            </div>
          </div>
          <button data-testid="profile-save" onClick={save} disabled={saving} className="btn-primary h-11 px-6 flex items-center gap-2 disabled:opacity-60">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save profile</button>
        </div>
      </div>

      <div className="rud-card rounded-2xl p-6 mt-4" data-testid="profile-orgsize">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-2"><Building2 size={14} /> Organization size</div>
        <p className="text-sm text-sec">Sets your cash-out threshold and tailors what the Engine suggests &amp; builds for you.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          {ORG_SIZES.map((o) => (
            <button key={o.key} data-testid={`profile-orgsize-${o.key}`} onClick={() => chooseOrgSize(o.key)}
              className={`rounded-xl px-3 py-2.5 text-left border transition-colors ${orgSize === o.key ? "border-brand bg-brand/10 text-brand" : "border-rud text-sec hover:border-brand/50"}`}>
              <div className="text-sm font-medium">{o.label}</div>
              <div className="text-[11px] text-sec leading-tight">{o.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <KindCircle />

      <div className="rud-card rounded-2xl p-6 mt-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-2"><Share2 size={14} /> Invite connections</div>
        <p className="text-sm text-sec">Share your invite link. When people you invite join, you grow your network and earn toward your cash-out threshold.</p>
        <div className="flex flex-col sm:flex-row gap-2 mt-3">
          <input readOnly value={inviteLink} data-testid="profile-invite-link" className="rud-input flex-1 rounded-lg px-3 py-2.5 text-sm" />
          <button data-testid="profile-copy-invite" onClick={copyInvite} className="btn-ghost rounded-lg h-11 px-4 flex items-center gap-2 text-sm"><Copy size={15} /> Copy</button>
          <button data-testid="profile-share-invite" onClick={shareInvite} className="btn-primary h-11 px-5 flex items-center gap-2 text-sm"><UserPlus size={15} /> Share invite</button>
        </div>
      </div>

      {genOpen && <GenModal lang={lang} onClose={() => setGenOpen(false)} onApply={(d) => { setP((prev) => ({ ...prev, headline: d.headline || prev.headline, bio: d.bio || prev.bio, skills: (d.skills && d.skills.length ? d.skills : prev.skills) })); setGenOpen(false); toast.success("Draft applied — review & save"); }} />}
    </div>
  );
}

function GenModal({ lang, onClose, onApply }) {
  const [posts, setPosts] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    if (!posts.trim() && !notes.trim()) { toast.error("Paste some posts or notes for My Kind to work with."); return; }
    setBusy(true);
    try { const { data } = await api.post("/profile/generate", { posts_text: posts, notes, lang }); onApply(data.draft); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[70] grid place-items-center p-4 bg-black/60" onClick={onClose}>
      <motion.div initial={{ scale: 0.96 }} animate={{ scale: 1 }} className="rud-card rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="profile-gen-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-2xl flex items-center gap-2"><Sparkles size={18} className="text-brand" /> My Kind, build my profile</h3>
          <button onClick={onClose} className="text-sec hover:text-[var(--rud-text)]"><X size={18} /></button>
        </div>
        <p className="text-sm text-sec mb-3">Paste a few of your social posts and any notes. My Kind will draft an authentic headline, bio and skills.</p>
        <textarea data-testid="gen-posts" value={posts} onChange={(e) => setPosts(e.target.value)} rows={5} placeholder="Paste your social posts here…" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm resize-none" />
        <textarea data-testid="gen-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Anything else about you (optional)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm resize-none mt-2" />
        <button data-testid="gen-run" onClick={gen} disabled={busy} className="btn-primary w-full h-11 mt-3 flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <><Loader2 size={16} className="animate-spin" /> Drafting…</> : <><Wand2 size={16} /> Draft my profile</>}</button>
      </motion.div>
    </motion.div>
  );
}
